/**
 * paymentCopy.ts — Route 1 (hold) vs Route 2 (immediate) checkout messaging.
 *
 * Route selection MUST follow server plan-preview state (`hasPlan` → clinician
 * plan in CheckoutFlow). Do not key off URL query params or recommendation
 * product keys: recommendations only filter the catalogue; payment timing is
 * decided by whether orders already has a pre-approved plan.
 */
export type PaymentCaptureRoute = 'hold' | 'immediate';

/** Map Orders `paymentPhase` (server-authoritative) to confirmation copy route. */
export function paymentPhaseToCaptureRoute(phase: 'held' | 'captured'): PaymentCaptureRoute {
  return phase === 'captured' ? 'immediate' : 'hold';
}

export type PaymentStepCopy = {
  addressHelp: string;
  paymentHelp: string;
  paymentAlert: string;
  /**
   * The route's own chip in the trust row, or null for no payment chip. The
   * hold route has none since 2026-09-26 — see `HOLD_PAYMENT`.
   */
  trustPaymentCue: string | null;
  placingLabel: string;
  placeOrderLabel: string;
  payError: string;
  /**
   * The attach failed and orders' answer never arrived (`attachOutcomeUnknown`):
   * the money may already be held or taken, so this NEVER says "not charged"
   * (lost attach response, journey UX review 2026-09-24).
   *
   * It tells the patient what is true on both lanes: the next press of the SAME
   * control is a check, not a second payment. That press re-attaches the SAME
   * SetupIntent — directly on the stub lane, and through Stripe's "already
   * succeeded" refusal on the Elements lane — and orders answers a retry of the
   * same intent without a second hold or a second capture (its month-1 hold and
   * capture are keyed on the order case). "Don't use a different card" is there
   * because a patient told nothing reaches for another card.
   *
   * ⚠️ "IT WILL NOT" IS UNCONDITIONAL, AND ONLY BECAUSE OF ORDERS (2026-09-25).
   * For a day it read "Checking now will not…" plus "if you ever see two holds,
   * contact us": the dedupe was Stripe's idempotency key alone
   * (`month1:{orderCaseId}:hold`), Stripe keeps keys for about 24 hours, and after
   * the Phase C 500 this copy exists for nothing else recorded the hold, so a check
   * a day later placed a SECOND hold (Route 2: took a second payment). Orders
   * `fix/month1-hold-reuses-payment-intent` closed that: before creating a hold,
   * both routes find the case's existing month-1 PaymentIntent at Stripe and record
   * it, and when Stripe cannot say, orders answers 503/500 with nothing new placed.
   * So a press however late is still only a check. RELEASE THAT ORDERS CHANGE
   * FIRST: against an orders build without it this sentence is false after a day.
   * If that reuse is ever removed, scope this promise again.
   *
   * NOT a decline. Since orders `fix/month1-hold-decline-is-400` a declined hold
   * is a 400 "Card authorisation failed", which maps to the "declined" copy. On an
   * older orders build a decline was a 500 and reached THIS sentence, on every
   * press: release that orders change first.
   */
  payOutcomeUnknown: string;
};

export type OrderConfirmationCopy = {
  help: string;
  alert: string;
  steps: ReadonlyArray<{ title: string; body: string }>;
};

/** Map clinician-built plan presence (from GET plan-preview) to payment route. */
export function paymentCaptureRoute(hasClinicianPlan: boolean): PaymentCaptureRoute {
  return hasClinicianPlan ? 'immediate' : 'hold';
}

const HOLD_PAYMENT: PaymentStepCopy = {
  addressHelp: 'Enter the address for your treatment. You can change it until a clinician approves.',
  // Orients only. The hold itself is explained by `CardHoldExplainer`, directly
  // below the order summary, so saying it here too would say it twice.
  paymentHelp: 'Check your order, then add your card below.',
  // Security only (2026-09-26, Ashley: "too much text"). The money half it used
  // to repeat is said right above it by `CardHoldExplainer` — a second telling
  // was the one the eye skipped.
  paymentAlert: 'Your card details are collected securely.',
  // NO PAYMENT CHIP (2026-09-26, the CTO: "we are saying the same thing over
  // and over"). "No charge until approved" in the trust row was the third
  // telling of one fact in one column: the rail's "Held on your card today" and
  // the hold box's approval point ("You only pay if a clinician approves. Not
  // approved? We release the hold and you pay nothing.") say it, in full words,
  // right above the chips. The row keeps its two service chips (UK clinicians,
  // GPhC-registered pharmacy) — the same two the plan screen shows.
  trustPaymentCue: null,
  placingLabel: 'Placing your order…',
  placeOrderLabel: 'Place order',
  payError: 'Sorry — we could not place your order. Your card has not been charged. Please try again.',
  payOutcomeUnknown:
    'We could not confirm whether your order went through, so a hold may already be on your card. ' +
    'Please do not pay with a different card — press Place order again to check. It will not place a second hold.',
};

/**
 * ROUTE 2 TAKES MONTH 1 TODAY, AND ONLY MONTH 1 (review, 2026-09-23). On attach
 * orders-service holds AND captures the preapproved case's stored monthly
 * (`r2a.monthlyPence`) — the first month — and activates the subscription,
 * which bills the rest month by month. The card screen now shows a term total
 * ("N-month total, billed monthly", `OrderSummary` `termTotalLabel`) beside a
 * "Paid today" row, so these sentences must name the FIRST MONTH: "authorises
 * immediate payment for your plan", read under a term total, says the whole
 * term is taken now.
 */
const IMMEDIATE_PAYMENT: PaymentStepCopy = {
  addressHelp: 'Enter the address for your treatment. You can update it from your dashboard after checkout.',
  paymentHelp:
    'Your clinician already reviewed and approved this treatment on your consultation. Confirming your plan takes your first month’s payment now — there is no second clinician approval after checkout.',
  paymentAlert:
    'Confirming takes your first month’s payment now. The rest of your plan is billed monthly. Card details are collected securely.',
  trustPaymentCue: 'Pay on confirmation',
  placingLabel: 'Confirming your payment…',
  placeOrderLabel: 'Confirm and pay',
  payError: 'Sorry — we could not complete payment. Please try again. If your card was charged, it will show in your dashboard.',
  payOutcomeUnknown:
    'We could not confirm whether your payment went through, so your first month may already have been taken. ' +
    'Please do not pay with a different card — press Confirm and pay again to check. It will not take a second payment.',
};

const HOLD_CONFIRMATION: OrderConfirmationCopy = {
  help: 'Thanks — your request is with our clinical team.',
  alert: 'Your card has not been charged. We placed a pre-authorisation hold only — payment is captured once a clinician approves your treatment. If declined, the hold is released.',
  steps: [
    {
      title: 'A UK clinician reviews your assessment',
      body: 'Usually within one working day. We may message you if we need anything else.',
    },
    {
      title: 'Once approved, we take payment and dispatch',
      body: 'Your card is only charged after approval. If declined, the hold is released and nothing is taken.',
    },
    {
      title: 'Track everything in your dashboard',
      body: 'Your plan, orders, billing and delivery address all live in one place.',
    },
  ],
};

const IMMEDIATE_CONFIRMATION: OrderConfirmationCopy = {
  help: 'Thanks — your clinician already approved this treatment. We are arranging dispatch.',
  alert: 'Payment has been authorised. There is no further clinician approval step after checkout.',
  steps: [
    {
      title: 'Payment authorised',
      body: 'Confirming your plan authorised payment. Your clinician already approved treatment during your consultation.',
    },
    {
      title: 'Pharmacy prepares and dispatches',
      body: 'A GPhC-registered pharmacy prepares your treatment and arranges delivery.',
    },
    {
      title: 'Track everything in your dashboard',
      body: 'Your plan, orders, billing and delivery address all live in one place.',
    },
  ],
};

export function paymentStepCopy(route: PaymentCaptureRoute): PaymentStepCopy {
  return route === 'immediate' ? IMMEDIATE_PAYMENT : HOLD_PAYMENT;
}

export function orderConfirmationCopy(route: PaymentCaptureRoute): OrderConfirmationCopy {
  return route === 'immediate' ? IMMEDIATE_CONFIRMATION : HOLD_CONFIRMATION;
}

/** Optional medication name inserted into the confirmation Help line. */
export function confirmationHelpText(route: PaymentCaptureRoute, medicationName?: string): string {
  const base = orderConfirmationCopy(route).help;
  if (!medicationName) return base;
  if (route === 'immediate') {
    return `Thanks — your ${medicationName} treatment was already approved by your clinician. We are arranging dispatch.`;
  }
  return `Thanks — your ${medicationName} request is with our clinical team.`;
}

export interface CardHoldPoint {
  /** Which fact this is — `CardHoldExplainer` picks the point's icon by it. Never shown. */
  key: 'approval' | 'change';
  lead: string;
  body: string;
}

/** The card-hold explainer's heading (below) — named because the checkout loader's short form restates it. */
export const CARD_HOLD_HEADING = 'This is a hold, not a payment';

/**
 * SHORT FORM of the hold, for the checkout loader card's third step headline
 * (2026-09-25, `checkoutLoaderContent.ts`; CTO: "use very catchy text"). The
 * same claim the card step makes, in fewer words and no more. ITS SOURCES, since
 * the 2026-09-26 dedupe removed two of the three it was first written against
 * (the rail's "Not a payment — you're only charged if a clinician approves your
 * treatment." and the explainer's "You don't pay today."):
 *   - {@link CARD_HOLD_HEADING}, "This is a hold, not a payment" — the hold
 *     box's heading, printed by `CardHoldExplainer`: HELD, NOT a payment;
 *   - the hold box's approval point, "You only pay if a clinician approves. Not
 *     approved? We release the hold and you pay nothing." — nothing is charged
 *     before a clinician says yes;
 *   - the payment rail's hold-route headline, "Held on your card today" — the
 *     CARD is what is held;
 *   - and the dashboard's own word for the same phase, "Not charged yet"
 *     (`lib/patientStatusCopy` `paymentPhaseCopy('held')`).
 * `checkoutLoaderContent.spec` checks every word of it against those sources,
 * which still print them. The loader pairs it with
 * `NO_CHARGE_UNTIL_APPROVED_SHORT` (serviceClaims.ts), so the condition is on the
 * card too. ⚠️ Never "£0 today" or "free": a pre-authorisation HOLD is placed,
 * and this line says so first. True only on the hold route — which is why the
 * loader carries it on the self-serve pre-plan lane alone (the clinician-led
 * route takes the first month at once).
 */
export const CARD_HELD_NOT_CHARGED = 'Card held, not charged';

/**
 * THE CARD-HOLD EXPLAINER (CTO, 2026-09-11), rendered by `CardHoldExplainer` on
 * the hold route's payment step. Every sentence is something orders-service does
 * TODAY (checked against its dev branch on 2026-09-11), and says only that:
 *   - The hold is month 1's price: checkout holds `monthlyPence` and captures nothing.
 *   - Capture happens only on a clinician's approval, and a rejection releases the
 *     hold (`releaseHold` in the reject path).
 *   - A different treatment is never charged against this hold. Approving a plan
 *     that differs from the one held returns the plan-link lane instead, so the
 *     patient confirms the new plan (and a new hold) before anything is taken.
 *
 * ⚠️ ON THAT LAST PATH THE FIRST HOLD IS NOT RELEASED BY US. It LAPSES: Stripe
 * cancels an uncaptured card authorisation after 7 days, and orders' own sweep
 * releases it at HOLD_LAPSE_DAYS (7). That is why the copy says it "drops off on
 * its own within 7 days" (it said "lapses" until 2026-09-26; the plainer word
 * means the same thing) and never "we release it" or "we adjust the amount". When
 * orders-service releases the old hold on a treatment change, change this
 * sentence in the same release, not before.
 *
 * NO FIGURE HERE ANY MORE (2026-09-26). The box's first point used to name
 * month 1's price ("You don't pay today. We just hold £X (your first month)"),
 * directly under the order summary's "Held on your card today £X (your first
 * month)". The summary is the one place the figure is printed now, so this copy
 * takes no amount, and there is no "£0" or `formatPence(undefined)` to guard
 * against here. ⚠️ With NO price the summary prints no figure either, so the
 * rail's hold headline says it in words instead — "Held on your card today /
 * Your first month's price" (`PaymentRail` `HELD_WITHOUT_FIGURE`, review
 * 2026-09-26) — the figure-free half of the old point, moved to the one place
 * that states what is held.
 *
 * SHORT AND IN THE PATIENT'S WORDS (Ashley Haveloch-Jones, via the CTO,
 * 2026-09-26: "too much text"; "talk to me like a person"; phrases about taking
 * money "never land with a customer"). Each point now leads with what the
 * patient KEEPS — "You only pay if a clinician approves" — in about half the
 * words. The lapse wording keeps its meaning exactly: the old hold goes "on its
 * own", never "we release it".
 */
export function cardHoldExplainerCopy(): { heading: string; points: CardHoldPoint[] } {
  return {
    heading: CARD_HOLD_HEADING,
    points: [
      {
        key: 'approval',
        lead: 'You only pay if a clinician approves.',
        body: 'Not approved? We release the hold and you pay nothing.',
      },
      {
        key: 'change',
        lead: 'Treatment changed? You only pay for the new one.',
        body:
          'You confirm the new plan first. The first hold drops off on its own within 7 days of being placed (some banks take a little longer to show it).',
      },
    ],
  };
}
